import { FormSuccess } from 'Components/Form/Success';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Main as Container } from 'Components/Main';
import { addUsersGroup, editUsersGroup, getUsersGroupById, updateUsersGroupUsers } from 'services/users-groups-service';
import { FormUsersGroup } from './formUsersGroup';
import { Dialog, PageHeader, Button } from 'Components/ui';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useQuery } from 'react-query';
import { getUsers } from 'services/users-service';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

// ─── Styled components ────────────────────────────────────────────────────────

const PageWrap = styled.div`
  padding-top: 16px;
  padding-bottom: 40px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const FormCard = styled.div`
  background: white;
  border-radius: 14px;
  border: 1px solid var(--vo-cloud);
  padding: 28px;
  max-width: 480px;
`;

const CardTitle = styled.h2`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 6px;
`;

const CardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0 0 24px;
  line-height: 1.5;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const UsersGroupsForm = () => {
  const { t } = useTranslation(['user']);
  const { id } = useParams();
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);
  const [idUsersGroup, setIdUsersGroup] = useState(null);
  const [finished, setFinished] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: { name: '', active: false },
  });

  const { data: allUsers, isLoading: isLoadingUsers } = useQuery(
    ['getAllUsers', selectedTenant.slug],
    async () => {
      const result = await getUsers(selectedTenant.slug, 1, 1000, '', '', true);
      return result?.data?.data || [];
    },
    { enabled: !!selectedTenant.slug }
  );

  const { data: userGroup } = useQuery(['getUsersGroups', id], async () => {
    if (!id) return null;
    const res = await getUsersGroupById(id);
    return res.data ?? null;
  });

  useEffect(() => {
    if (!allUsers) return;
    if (id && userGroup) {
      const groupName = userGroup.name;
      const inGroup = allUsers.filter(u =>
        u.groups?.some(g => g.companyKey === selectedTenant.slug && g.name === groupName)
      );
      const notInGroup = allUsers.filter(u =>
        !u.groups?.some(g => g.companyKey === selectedTenant.slug && g.name === groupName)
      );
      setSelectedUsers(inGroup);
      setAvailableUsers(notInGroup);
    } else if (!id) {
      setAvailableUsers(allUsers);
      setSelectedUsers([]);
    }
  }, [allUsers, id, userGroup, selectedTenant.slug]);

  useEffect(() => {
    if (!id || !userGroup) return;
    Object.keys(userGroup).forEach(key => {
      if (key !== 'users') setValue(key, userGroup[key]);
    });
  }, [id, userGroup, setValue]);

  const handleComplete = async () => {
    try {
      const values = getValues();
      const payload = {
        name: values.name,
        active: values.active,
        companyKey: selectedTenant.slug,
      };
      const res = id
        ? await editUsersGroup(id, payload)
        : await addUsersGroup(payload);

      if (res.status === 200 || res.status === 201) {
        const savedGroupId = res.data._id ?? id;
        setIdUsersGroup(savedGroupId);

        await updateUsersGroupUsers(savedGroupId, selectedUsers.map(u => u._id));

        Dialog.alert({
          title: t('user:alert.successTitle'),
          message: t('user:groups.success.saved'),
          icon: 'success',
        });

        if (!id) setFinished(true);
      } else {
        throw new Error('Erro ao salvar o grupo de usuários.');
      }
    } catch (error) {
      Dialog.alert({
        title: t('user:alert.errorTitle'),
        message: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const handleReset = () => {
    reset();
    setFinished(false);
    setIdUsersGroup(null);
    navigate('/users-groups/add');
  };

  return (
    <Container>
      <PageWrap>
        <PageHeader
          title={id ? t('user:groups.form.editTitle') : t('user:groups.form.addTitle')}
          segments={[
            { label: t('common:breadcrumbs.manage') },
            { label: t('user:groups.title'), route: '/users-groups' },
            { label: id ? t('user:groups.form.editTitle') : t('user:groups.form.addTitle') },
          ]}
          actions={
            !finished && (
              <HeaderActions data-tour="form-actions">
                <Button variant="outline" type="button" onClick={() => navigate('/users-groups')} sx={{ height: '44px', padding: '0 20px' }}>
                  {t('user:groups.form.cancel')}
                </Button>
                <Button variant="primary" type="submit" form="users-group-form" sx={{ height: '44px', padding: '0 24px' }}>
                  {t('user:groups.form.save')}
                </Button>
              </HeaderActions>
            )
          }
        />

        {!finished ? (
          <FormCard>
            <CardTitle>{t('user:groups.form.cardTitle')}</CardTitle>
            <CardSubtitle>{t('user:groups.form.cardSubtitle')}</CardSubtitle>

            <FormUsersGroup
              register={register}
              handleSubmit={handleSubmit}
              handleComplete={handleComplete}
              errors={errors}
              control={control}
              availableUsers={availableUsers}
              selectedUsers={selectedUsers}
              isLoadingUsers={isLoadingUsers}
              onUsersChange={(newSelected, newAvailable) => {
                setSelectedUsers(newSelected);
                setAvailableUsers(newAvailable);
              }}
            />
          </FormCard>
        ) : (
          <FormCard>
            <FormSuccess
              title={t('user:groups.form.successTitle')}
              labelStartAgain={t('user:groups.form.addAnother')}
              handleStartAgain={handleReset}
              labelShow={t('user:groups.form.viewDetails')}
              handleShow={() => navigate('/users-groups/show/' + idUsersGroup)}
            />
          </FormCard>
        )}
      </PageWrap>
    </Container>
  );
};
